import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BTN_PRIMARY, BTN_OUTLINE } from "@/lib/theme";
import {
  Pizza, Percent, Star, Check, Store, Gift, Wallet, ShieldCheck, CreditCard, QrCode,
  ArrowRight, Calculator, Clock, Plug, Megaphone, Zap, Smartphone, Quote,
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1765991734864-b1572d31ad86?crop=entropy&cs=srgb&fm=webp&q=80&w=1600&auto=format,compress";
const OWNER = "https://images.unsplash.com/photo-1753351052363-53ce102830eb?crop=entropy&cs=srgb&fm=webp&q=80&w=1200&auto=format,compress";
const BUSINESSES = [
  { src: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&fm=webp&q=80&fit=crop", label: "Pizzerias" },
  { src: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800&fm=webp&q=80&fit=crop", label: "Cafés & coffee shops" },
  { src: "https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=800&fm=webp&q=80&fit=crop", label: "Ice cream shops" },
  { src: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800&fm=webp&q=80&fit=crop", label: "Barbershops" },
];

const NAVY = "text-[#17324D]";
const MUTED = "text-[#17324D]/70";

const PrimaryBtn = ({ to, children, testid, className = "" }) => (
  <Link to={to} data-testid={testid} className={`${BTN_PRIMARY} ${className}`}>{children}</Link>
);

const OutlineBtn = ({ to, children, testid, className = "" }) => (
  <Link to={to} data-testid={testid} className={`${BTN_OUTLINE} ${className}`}>{children}</Link>
);

const StickyMobileCta = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const hero = document.querySelector("[data-testid=hero-cta-owner]");
    if (!hero) return;
    const io = new IntersectionObserver(([e]) => setShow(!e.isIntersecting), { threshold: 0 });
    io.observe(hero);
    return () => io.disconnect();
  }, []);
  return (
    <div data-testid="sticky-mobile-cta"
      className={`md:hidden fixed inset-x-0 bottom-0 z-[70] bg-white/95 backdrop-blur border-t border-[#17324D]/10 px-4 py-3 pr-[4.5rem] transition-transform duration-300 ${show ? "translate-y-0" : "translate-y-full"}`}>
      <Link to="/register" data-testid="sticky-cta-btn" className={`${BTN_PRIMARY} w-full py-3 text-sm shadow-none hover:shadow-none hover:translate-x-0 hover:translate-y-0`}>
        Start Your Regulars Club <ArrowRight size={16} />
      </Link>
    </div>
  );
};

const Overline = ({ children }) => (
  <p className="text-xs sm:text-sm font-bold tracking-[0.18em] uppercase text-[#D94A3D]">{children}</p>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] ${NAVY} ${className}`}>{children}</h2>
);

const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`bg-white border border-[#17324D]/10 rounded-2xl shadow-[0_1px_3px_rgba(23,50,77,0.06)] ${className}`}>{children}</div>
);

const IconBadge = ({ icon: Icon }) => (
  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#D94A3D]/10 text-[#D94A3D]"><Icon size={22} strokeWidth={2.25} /></span>
);

const SectionCta = ({ testid, label = "Start Your Regulars Club" }) => (
  <div className="mt-12 flex justify-center">
    <PrimaryBtn to="/register" testid={testid}>{label} <ArrowRight size={18} /></PrimaryBtn>
  </div>
);

export default function Landing() {
  return (
    <div className="App bg-[#FFF4E6] text-[#17324D] pb-16 md:pb-0">
      <Navbar />
      <StickyMobileCta />

      {/* ============ HERO ============ */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <img src={HERO_BG} alt="" aria-hidden="true" fetchPriority="high" decoding="async"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#FFF4E6]/[0.92]" />
        <div className="relative max-w-6xl mx-auto px-5 py-24 md:py-36 text-center">
          <h1 className={`font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] ${NAVY} max-w-4xl mx-auto fade-up`}>
            Your favorite restaurants. Exclusive perks every month.
          </h1>
          <p className={`mt-6 text-base md:text-lg ${MUTED} max-w-2xl mx-auto leading-relaxed fade-up`}>
            Join your local restaurant's loyalty club and get free items, special offers, and rewards — just for being a regular.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center fade-up">
            <PrimaryBtn to="/register" testid="hero-cta-owner" className="w-full sm:w-auto">Start Your Regulars Club <ArrowRight size={18} /></PrimaryBtn>
            <OutlineBtn to="/clubs" testid="hero-cta-diner" className="w-full sm:w-auto">Find a Regulars Club</OutlineBtn>
          </div>
          <p className={`mt-8 text-sm ${MUTED} flex flex-wrap justify-center gap-x-6 gap-y-2`}>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={15} /> Secure Stripe payments</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={15} /> Cancel anytime</span>
            <span className="inline-flex items-center gap-1.5"><Plug size={15} /> No POS integration needed</span>
          </p>
          <p className="mt-4 text-sm">
            <Link to="/calculator" data-testid="hero-calculator-link" className="inline-flex items-center gap-1.5 font-semibold text-[#D94A3D] underline underline-offset-4 hover:text-[#C33E32]">
              <Calculator size={15} /> Calculate your revenue potential
            </Link>
          </p>
        </div>
      </section>

      {/* ============ Trust row ============ */}
      <section className="bg-white border-y border-[#17324D]/10">
        <div className="max-w-6xl mx-auto px-5 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Secure Stripe payments", d: "PCI-compliant, card data never touches us." },
            { icon: Clock, t: "Cancel anytime", d: "No contracts or lock-ins." },
            { icon: Plug, t: "No POS integration", d: "Works alongside what you already use." },
            { icon: CreditCard, t: "Digital membership cards", d: "Members show a live-verified card on their phone." },
          ].map((x, i) => (
            <div key={i} data-testid={`trust-item-${i}`} className="flex items-start gap-3">
              <x.icon size={22} className="text-[#D94A3D] shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-sm">{x.t}</p>
                <p className={`text-sm ${MUTED} mt-0.5`}>{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ How it works ============ */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-28">
        <div className="max-w-2xl">
          <Overline>How it works</Overline>
          <H2 className="mt-3">Three steps to recurring revenue.</H2>
          <p className={`mt-4 text-base md:text-lg ${MUTED}`}>Go live in an afternoon. We handle billing, renewals, and failed-payment recovery.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { num: "1", icon: Store, t: "Create Your Club", d: "Sign up, connect Stripe, and set your monthly price — most restaurants start around $30/mo." },
            { num: "2", icon: Gift, t: "Add Your Perks", d: "Free monthly items, member discounts, VIP specials, priority access — you decide." },
            { num: "3", icon: Wallet, t: "Earn Recurring Revenue", d: "Members are billed automatically every month. Payouts go straight to your bank." },
          ].map((s, i) => (
            <div key={i} data-testid={`how-step-${i}`} className="relative">
              {i < 2 && <span aria-hidden="true" className="hidden md:block absolute top-6 left-24 right-[-1.5rem] h-px bg-[#17324D]/15" />}
              <div className="flex items-center gap-4">
                <span className="relative z-10 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D94A3D] text-white font-heading font-extrabold text-lg">{s.num}</span>
                <s.icon size={24} strokeWidth={2} className="text-[#17324D]/60" />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl mt-6">{s.t}</h3>
              <p className={`${MUTED} mt-2 leading-relaxed max-w-sm`}>{s.d}</p>
            </div>
          ))}
        </div>
        <SectionCta testid="how-cta" />
      </section>

      {/* ============ Perks + business types ============ */}
      <section className="bg-white border-y border-[#17324D]/10">
        <div className="max-w-6xl mx-auto px-5 py-20 md:py-28">
          <div className="max-w-2xl">
            <Overline>Your perks, your rules</Overline>
            <H2 className="mt-3">You choose the price. You choose the benefits.</H2>
            <p className={`mt-4 text-base md:text-lg ${MUTED}`}>Popular perks restaurants offer their regulars:</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { icon: Pizza, t: "Free monthly items", d: "One free item every month, e.g. a large cheese pizza." },
              { icon: Percent, t: "Member discounts", d: "A set percentage off every order." },
              { icon: Star, t: "VIP specials", d: "Secret member-only deals each week." },
              { icon: Zap, t: "Priority access", d: "Skip the line, first dibs on events and new menu items." },
            ].map((p, i) => (
              <div key={i} data-testid={`perk-card-${i}`}>
                <IconBadge icon={p.icon} />
                <h3 className="font-heading font-bold text-lg mt-5">{p.t}</h3>
                <p className={`text-sm ${MUTED} mt-2 leading-relaxed`}>{p.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-20">
            <Overline>Built for local businesses</Overline>
            <H2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl">Works for every neighborhood favorite.</H2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              {BUSINESSES.map((b, i) => (
                <figure key={i} data-testid={`business-type-${i}`} className="relative rounded-2xl overflow-hidden border border-[#17324D]/10 h-48 md:h-56">
                  <img src={b.src} alt={b.label} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-[#17324D]/85 text-white text-sm font-bold px-4 py-2.5">{b.label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
          <SectionCta testid="perks-cta" />
        </div>
      </section>

      {/* ============ Case study — Biaggio ============ */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <Overline>Real results</Overline>
          <H2 className="mt-3">Biaggio Pizzeria pays $40/mo and keeps <span className="text-[#D94A3D]">$260</span> in recurring profit.</H2>
          <Card testid="case-study-receipt" className="mt-8 p-6 max-w-md">
            <p className="font-heading font-bold text-lg border-b border-dashed border-[#17324D]/20 pb-3">Monthly ledger</p>
            <div className="mt-4 space-y-3">
              <div className="flex justify-between"><span>10 members × $30/mo</span><span className="font-bold">+$300</span></div>
              <div className={`flex justify-between ${MUTED}`}><span>Platform fee</span><span className="font-bold">−$40</span></div>
              <div className="flex justify-between border-t border-[#17324D]/20 pt-3 text-lg">
                <span className="font-heading font-bold">Net recurring profit</span>
                <span className="font-heading font-extrabold text-[#D94A3D]">$260/mo</span>
              </div>
            </div>
          </Card>
          <p className={`text-xs ${MUTED} mt-3`}>Real numbers from a founding restaurant. Your results depend on your members and pricing.</p>
          <PrimaryBtn to="/register" testid="case-cta" className="mt-8">Start Your Regulars Club <ArrowRight size={18} /></PrimaryBtn>
        </div>
        <div>
          <div className="rounded-2xl overflow-hidden border border-[#17324D]/10">
            <img src={OWNER} alt="Restaurant owner" loading="lazy" decoding="async" className="w-full h-[280px] md:h-[340px] object-cover" />
          </div>
          <Card testid="owner-testimonial" className="mt-6 p-7 relative">
            <Quote size={36} className="absolute -top-4 left-6 text-[#D94A3D] fill-[#D94A3D]" />
            <blockquote className="font-heading font-semibold text-lg md:text-xl leading-snug mt-2">
              “The Regulars Club has helped Biaggio Pizzeria reward our most loyal customers while creating dependable recurring revenue for our business. It’s simple to manage, easy for our customers to use, and gives people another reason to keep coming back.”
            </blockquote>
            <figcaption className={`mt-4 text-sm ${MUTED}`}>
              <span className="font-bold text-[#17324D]">Owner, Biaggio Pizzeria</span> · Founding Regulars Club restaurant
            </figcaption>
          </Card>
        </div>
      </section>

      {/* ============ Pricing ============ */}
      <section className="bg-[#17324D] text-white">
        <div className="max-w-6xl mx-auto px-5 py-20 md:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <Overline>Pricing</Overline>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] mt-3">Simple pricing. You keep your membership revenue.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
            {/* Diner plan */}
            <div data-testid="pricing-card-diner" className="bg-white/5 border border-white/15 rounded-2xl p-8 flex flex-col">
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-white/70">For diners</p>
              <h3 className="font-heading font-bold text-2xl mt-2">Club Member</h3>
              <p className="font-heading font-extrabold text-5xl leading-none mt-5">Free<span className="text-base font-semibold text-white/70"> to join</span></p>
              <p className="text-sm text-white/70 mt-2">Each restaurant sets its own membership price (e.g. $30/mo), paid directly to the restaurant.</p>
              <ul className="space-y-3 mt-6 flex-1">
                {["Digital membership card with live verification", "Monthly free item voucher", "Member discounts on every order", "Exclusive weekly deals", "No app to download"].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-medium">
                    <span className="bg-white/15 rounded-full p-0.5"><Check size={13} strokeWidth={3} /></span>{f}
                  </li>
                ))}
              </ul>
              <Link to="/clubs" data-testid="pricing-diner-cta" className={`${BTN_OUTLINE} mt-8 w-full !text-white !border-white hover:!bg-white hover:!text-[#17324D]`}>Find a Regulars Club</Link>
            </div>
            {/* Restaurant plan — recommended */}
            <div data-testid="pricing-card" className="relative bg-white text-[#17324D] rounded-2xl p-8 flex flex-col shadow-[0_8px_30px_rgba(0,0,0,0.18)] ring-2 ring-[#D94A3D]">
              <span data-testid="pricing-recommended-badge" className="absolute -top-3 left-8 bg-[#D94A3D] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Recommended</span>
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#D94A3D]">For restaurants</p>
              <h3 className="font-heading font-bold text-2xl mt-2">Restaurant Platform</h3>
              <p className="font-heading font-extrabold text-5xl leading-none mt-5 text-[#D94A3D]">$40<span className={`text-base font-semibold ${MUTED}`}>/month per location</span></p>
              <p className={`text-sm ${MUTED} mt-2`}>Set your own membership price and keep every dollar your members pay.</p>
              <ul className="space-y-3 mt-6 flex-1">
                {["Set your own membership price", "Unlimited members", "Direct Stripe Connect payouts", "Automated billing + failed-payment recovery", "Member CSV export", "Shareable QR marketing kit"].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-medium">
                    <span className="bg-[#D94A3D] text-white rounded-full p-0.5"><Check size={13} strokeWidth={3} /></span>{f}
                  </li>
                ))}
              </ul>
              <PrimaryBtn to="/register" testid="pricing-cta" className="mt-8 w-full">Start Your Regulars Club <ArrowRight size={18} /></PrimaryBtn>
              <Link to="/calculator" data-testid="pricing-calculator-cta" className="mt-4 flex items-center justify-center gap-1.5 text-sm font-semibold text-[#D94A3D] underline underline-offset-4 hover:text-[#C33E32]">
                <Calculator size={15} /> Calculate your revenue potential
              </Link>
            </div>
          </div>
          <p className="text-center text-sm text-white/60 mt-8">Cancel anytime · No contracts · Secure payments by Stripe</p>
        </div>
      </section>

      {/* ============ For diners ============ */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-28">
        <div className="max-w-2xl">
          <Overline>For diners</Overline>
          <H2 className="mt-3">Why your customers will love it.</H2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 mt-12">
          {[
            { icon: Smartphone, t: "Digital membership card", d: "Live verification with a QR code, right on their phone. No app to download — just show it at the counter." },
            { icon: Pizza, t: "Monthly freebies", d: "A free item every month, just for being a regular." },
            { icon: Percent, t: "Member discounts", d: "Automatic percentage off every check." },
            { icon: Megaphone, t: "Exclusive weekly deals", d: "Secret member-only specials that bring them back." },
          ].map((p, i) => (
            <div key={i} data-testid={i === 0 ? "bento-card" : `diner-card-${i}`} className="flex gap-5">
              <IconBadge icon={p.icon} />
              <div>
                <h3 className="font-heading font-bold text-xl">{p.t}</h3>
                <p className={`${MUTED} mt-2 leading-relaxed`}>{p.d}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <OutlineBtn to="/clubs" testid="diner-cta">Find a Regulars Club <QrCode size={18} /></OutlineBtn>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="bg-white border-y border-[#17324D]/10">
        <div className="max-w-3xl mx-auto px-5 py-20 md:py-28">
          <H2 className="text-center">Questions? Answered.</H2>
          <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
            {[
              { q: "What is The Regulars Club?", a: "The Regulars Club is a restaurant membership platform that connects diners with participating restaurants offering exclusive membership perks and benefits." },
              { q: "How does The Regulars Club work?", a: "Customers can browse participating restaurant clubs, choose a club they want to join, create an account, and access the membership benefits offered by that restaurant." },
              { q: "How do I find participating restaurants?", a: "Visit the Browse Clubs section to discover participating restaurant membership clubs and view the benefits available from each restaurant." },
              { q: "How do I join a restaurant's club?", a: "Choose a participating restaurant from Browse Clubs, review its membership offer, and follow the signup process to join." },
              { q: "What benefits do restaurant club members receive?", a: "Benefits vary by restaurant. Each participating restaurant determines the perks, rewards, or special offers included with its membership." },
              { q: "Can restaurants join The Regulars Club?", a: "Yes. The Regulars Club is designed to help restaurants create membership programs that encourage repeat customers and recurring relationships." },
              { q: "How do payouts work?", a: "Membership payments are routed directly to your own bank account via Stripe Connect. The Regulars Club never holds your money." },
              { q: "What does it cost?", a: "A flat $40/month platform fee per location. You set your own membership price (e.g. $30/mo) and keep 100% of that revenue." },
              { q: "Can I cancel anytime?", a: "Yes — there are no lock-in contracts. Cancel whenever you like from your dashboard." },
              { q: "Is it secure?", a: "All payments are processed securely by Stripe, an industry-leading, PCI-compliant payment provider. Card details never touch our servers." },
              { q: "Do my customers need an app?", a: "No. Members get a digital membership card with live verification and a QR code in their browser — nothing to download." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="bg-[#FFF4E6] border border-[#17324D]/10 rounded-xl px-5">
                <AccordionTrigger className="text-left font-semibold text-base hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className={`${MUTED} text-base`}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <SectionCta testid="faq-cta" />
        </div>
      </section>

      {/* ============ Final CTA ============ */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-28 text-center">
        <H2>Ready to make regulars?</H2>
        <p className={`mt-4 text-base md:text-lg ${MUTED}`}>Start your club today, or find one near you.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <PrimaryBtn to="/register" testid="final-cta-owner">Start Your Regulars Club <ArrowRight size={18} /></PrimaryBtn>
          <OutlineBtn to="/clubs" testid="final-cta-diner">Find a Regulars Club</OutlineBtn>
        </div>
      </section>

      {/* ============ Footer ============ */}
      <footer data-testid="footer" className="bg-white border-t border-[#17324D]/10 text-[#17324D]">
        <div className="max-w-6xl mx-auto px-5 py-14 grid gap-10 sm:grid-cols-2 md:grid-cols-4">
          <div className="sm:col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2">
              <span className="bg-[#D94A3D] text-white p-1.5 rounded-lg"><Pizza size={18} strokeWidth={2.5} /></span>
              <span className="font-heading font-extrabold text-lg tracking-tight">Regulars Club</span>
            </Link>
            <p className={`text-sm ${MUTED} mt-4 max-w-xs`}>Paid membership clubs for local restaurants.</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#17324D]/50">Product</p>
            <ul className="mt-4 space-y-2.5 text-sm font-medium">
              <li><Link to="/clubs" data-testid="footer-clubs" className="hover:text-[#D94A3D]">Browse Clubs</Link></li>
              <li><Link to="/register" data-testid="footer-register" className="hover:text-[#D94A3D]">Start Your Club</Link></li>
              <li><Link to="/calculator" data-testid="footer-calculator" className="hover:text-[#D94A3D]">Revenue Calculator</Link></li>
              <li><Link to="/blog" data-testid="footer-blog" className="hover:text-[#D94A3D]">Blog</Link></li>
              <li><Link to="/login" data-testid="footer-login" className="hover:text-[#D94A3D]">Login</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#17324D]/50">Legal</p>
            <ul className="mt-4 space-y-2.5 text-sm font-medium">
              <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#D94A3D]">Privacy Policy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms" className="hover:text-[#D94A3D]">Terms of Service</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#17324D]/50">Contact</p>
            <ul className="mt-4 space-y-2.5 text-sm font-medium">
              <li><a href="mailto:Theregularsclubmember@gmail.com" data-testid="footer-email" className="hover:text-[#D94A3D] break-all">Theregularsclubmember@gmail.com</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#17324D]/10">
          <div className={`max-w-6xl mx-auto px-5 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs ${MUTED}`}>
            <span>© {new Date().getFullYear()} The Regulars Club. All rights reserved.</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#D94A3D]" /> Payments secured by Stripe</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
